import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBriefcase,
  FaIdCard,
  FaKeyboard,
  FaRocket,
  FaFileAlt,
  FaExclamationTriangle
} from 'react-icons/fa';
import Loader from '../components/common/Loader';
import JobDescriptionInput from '../components/tailor/JobDescriptionInput';
import ProfileResumeSelector from '../components/tailor/ProfileResumeSelector';
import CustomPromptInput from '../components/tailor/CustomPromptInput';
import TailoringProcess from '../components/tailor/TailoringProcess';
import EnhancedResultsView from '../components/tailor/EnhancedResultsView';
import tailorService from '../services/tailor';
import './TailorResume.css';

const TailorResume = () => {

  useEffect(() => {
    document.title += ' - Tailor';
    // This will clean up the title when component unmounts
    return () => {
      document.title = 'CVance'; // Default title
    };
  }, []);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Data states
  const [sessionId, setSessionId] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [tailoringResults, setTailoringResults] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    jobDescription: '',
    jobTitle: '',
    company: '',
    selectedProfileId: null,
    selectedResumeId: null,
    customPrompt: ''
  });

  // Session status
  const [processingStatus, setProcessingStatus] = useState({
    status: 'pending',
    progress: 0
  });

  // Fetch profiles and resumes on component mount
  useEffect(() => {
    if (step === 2) {
      fetchProfilesAndResumes();
    }
  }, [step]);

  // Poll for status when in processing step
  useEffect(() => {
    let intervalId;

    if (step === 4 && sessionId) {
      intervalId = setInterval(() => {
        checkSessionStatus();
      }, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [step, sessionId]);

  // Fetch profiles and resumes
  const fetchProfilesAndResumes = async () => {
    setIsLoading(true);
    try {
      const [profilesResponse, resumesResponse] = await Promise.all([
        tailorService.getUserProfiles(),
        tailorService.getUserResumes()
      ]);

      setProfiles(profilesResponse || []);
      setResumes(resumesResponse || []);
    } catch (err) {
      setError('Failed to load your profiles and resumes.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change for form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle text area change
  const handleTextAreaChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle profile/resume selection
  const handleSelectionChange = (type, id) => {
    setFormData(prev => ({
      ...prev,
      [type]: id
    }));
  };

  // Step 1: Start tailoring session
  const startTailoringSession = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Validate
      if (!formData.jobDescription.trim()) {
        throw new Error('Please enter a job description');
      }

      // Prepare session data
      const sessionData = {
        job_description: formData.jobDescription,
        custom_input: '', // Will be updated later
        job_title: formData.jobTitle || undefined,
        company: formData.company || undefined
      };

      // Start session
      const response = await tailorService.startTailoringSession(sessionData);

      if (response?.session_id) {
        setSessionId(response.session_id);
        setStep(2); // Proceed to profile/resume selection
      } else {
        throw new Error('Failed to create tailoring session');
      }
    } catch (err) {
      setError(err.message || 'Failed to start tailoring session');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Update session with profile/resume selection
  const updateSessionWithSelection = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Validate
      if (!formData.selectedProfileId && !formData.selectedResumeId) {
        throw new Error('Please select a profile or resume');
      }

      // Prepare update data
      const updateData = {};

      if (formData.selectedProfileId) {
        updateData.profile_id = formData.selectedProfileId;
      }

      if (formData.selectedResumeId) {
        updateData.resume_id = formData.selectedResumeId;
      }

      // Update session
      await tailorService.updateSession(sessionId, updateData);

      // Proceed to custom prompt
      setStep(3);
    } catch (err) {
      setError(err.message || 'Failed to update selection');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Update session with custom prompt and process
  const updateAndProcessSession = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Update with custom prompt if provided
      if (formData.customPrompt.trim()) {
        await tailorService.updateSession(sessionId, {
          custom_input: formData.customPrompt
        });
      }

      // Process the session
      await tailorService.processTailoringSession(sessionId);

      // Move to processing step
      setStep(4);
    } catch (err) {
      setError(err.message || 'Failed to process tailoring session');
    } finally {
      setIsLoading(false);
    }
  };

  // Check session status
  const checkSessionStatus = async () => {
    try {
      const sessionDetail = await tailorService.getTailoringSession(sessionId);

      if (sessionDetail) {
        const { status } = sessionDetail;

        // Calculate progress based on status
        let progress = 0;
        switch (status) {
          case 'pending':
            progress = 10;
            break;
          case 'Analysing Job Description':
            progress = 30;
            break;
          case 'matching':
            progress = 50;
            break;
          case 'Tailoring Resume':
            progress = 70;
            break;
          case 'finalizing':
            progress = 90;
            break;
          case 'completed':
            progress = 100;
            fetchResults();
            break;
          case 'failed':
            setError('Processing failed. Please try again.');
            break;
          default:
            progress = processingStatus.progress;
        }

        setProcessingStatus({
          status,
          progress
        });
      }
    } catch (err) {
      console.error('Error checking status:', err);
    }
  };

  // Fetch tailoring results
  const fetchResults = async () => {
    try {
      const results = await tailorService.getTailoringResult(sessionId);
      setTailoringResults(results);
      setStep(5); // Move to results
    } catch (err) {
      setError(err.message || 'Failed to fetch results');
    }
  };

  // Handle download
  const handleDownload = async (format = 'pdf') => {
    try {
      await tailorService.downloadTailoredResume(sessionId, format);
    } catch (err) {
      setError(err.message || `Failed to download ${format.toUpperCase()}`);
    }
  };

  // Handle regenerate
  const handleRegenerate = async () => {
    try {
      setStep(4); // Back to processing
      setProcessingStatus({
        status: 'pending',
        progress: 0
      });

      // Process the session again
      await tailorService.processTailoringSession(sessionId);
    } catch (err) {
      setError(err.message || 'Failed to regenerate resume');
    }
  };

  // Next step handler
  const handleNext = () => {
    switch (step) {
      case 1:
        startTailoringSession();
        break;
      case 2:
        updateSessionWithSelection();
        break;
      case 3:
        updateAndProcessSession();
        break;
      default:
        setStep(prev => prev + 1);
    }
  };

  // Previous step handler
  const handlePrev = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  // Get step icon
  const getStepIcon = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return <FaBriefcase />;
      case 2:
        return <FaIdCard />;
      case 3:
        return <FaKeyboard />;
      case 4:
        return <FaRocket />;
      case 5:
        return <FaFileAlt />;
      default:
        return null;
    }
  };

  // Render current step
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <JobDescriptionInput
            jobDescription={formData.jobDescription}
            jobTitle={formData.jobTitle}
            company={formData.company}
            onChange={handleInputChange}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <ProfileResumeSelector
            profiles={profiles}
            resumes={resumes}
            selectedProfileId={formData.selectedProfileId}
            selectedResumeId={formData.selectedResumeId}
            onSelectionChange={handleSelectionChange}
            onPrev={handlePrev}
            onNext={handleNext}
            isLoading={isLoading}
            refreshProfiles={fetchProfilesAndResumes}
          />
        );
      case 3:
        return (
          <CustomPromptInput
            customPrompt={formData.customPrompt}
            jobTitle={formData.jobTitle}
            company={formData.company}
            onChange={handleTextAreaChange}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        );
      case 4:
        return (
          <TailoringProcess
            status={processingStatus.status}
            progress={processingStatus.progress}
            jobTitle={formData.jobTitle}
            company={formData.company}
          />
        );
      case 5:
        return (
          <EnhancedResultsView
            data={tailoringResults}
            jobTitle={formData.jobTitle}
            company={formData.company}
            onDownloadPdf={() => handleDownload('pdf')}
            onDownloadDocx={() => handleDownload('docx')}
            onRegenerate={handleRegenerate}
            onDashboard={() => navigate('/dashboard')}
          />
        );
      default:
        return null;
    }
  };

  // Loading indicator
  if (isLoading && step !== 4) {
    return <Loader fullScreen text="Processing your request..." />;
  }

  return (
    <div className="tailor-resume-container">
      <div className="tailor-header">
        <h1>Tailor Your Resume</h1>
        <p className="tailor-subtitle">
          Customize your resume to match the job description and stand out from other applicants
        </p>
      </div>

      {error && (
        <div className="error-message">
          <FaExclamationTriangle /> {error}
        </div>
      )}

      <div className="tailor-steps-container">
        <div className="tailor-steps">
          {[1, 2, 3, 4, 5].map((num) => (
            <div
              key={num}
              className={`step ${step >= num ? 'active' : ''} ${step > num ? 'completed' : ''}`}
            >
              <div className="step-icon">
                {getStepIcon(num)}
              </div>
              <div className="step-label">
                {num === 1 && 'Job Details'}
                {num === 2 && 'Select Source'}
                {num === 3 && 'Custom Instructions'}
                {num === 4 && 'Processing'}
                {num === 5 && 'Tailored Resume'}
              </div>
              {num < 5 && <div className="step-connector"></div>}
            </div>
          ))}
        </div>
      </div>

      <div className="tailor-content-container">
        {renderStep()}
      </div>
    </div>
  );
};

export default TailorResume;